import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, RefreshCcw, ChevronDown, ChevronUp, Radio, PackagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { PreviewPlayButton, stopPreview, setPreviewQueue, clearPreviewQueue, subscribePreview } from './PreviewPlayButton';
import RailTrackActions from './RailTrackActions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Last.fm serves these grey star placeholders instead of real art — treat them as "no cover".
const LASTFM_PLACEHOLDERS = ['2a96cbd8b46e442fc41c2b86b821562f', 'c6f59c1e5e7240a4c0d427abd71f3dbb', '4128a6eb29f94943c9d206c08e625904'];
const isRealCover = (url) => !!url && url.startsWith('http') && !LASTFM_PLACEHOLDERS.some(h => url.includes(h));

// Deterministic tint per artist so fallback tiles look designed, not broken.
const TINTS = [['#0f766e', '#082f49'], ['#4c1d95', '#1e1b4b'], ['#9d174d', '#3b0764'], ['#065f46', '#0c4a6e'], ['#7c2d12', '#431407'], ['#1e3a8a', '#172554']];
const tintFor = (seed = '') => {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) % 997;
  return TINTS[h % TINTS.length];
};

const CoverTile = ({ src, seed, label, children }) => {
  const [failed, setFailed] = useState(false);
  const [a, b] = tintFor(seed);
  const show = isRealCover(src) && !failed;
  return (
    <div style={{
      width: '100%', aspectRatio: '1 / 1', borderRadius: '7px', overflow: 'hidden',
      background: `linear-gradient(135deg, ${a}, ${b})`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
    }}>
      {show ? (
        <img src={src} alt={label} loading="lazy" onError={() => setFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <span data-testid="similar-cover-fallback" style={{ color: 'rgba(255,255,255,0.78)', fontSize: '20px', fontWeight: 800, letterSpacing: '-0.02em', textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
          {(label?.charAt(0) || '?').toUpperCase()}
        </span>
      )}
      {children}
    </div>
  );
};

/**
 * LastFmSimilarTracksRail — Last.fm's similar engine (electronic-gated server-side),
 * collapsed behind a show/hide header so it never competes with BEATSEEK AI's
 * "Find Similar". Covers are enriched server-side (Deezer/iTunes) because Last.fm
 * returns the same placeholder for most tracks.
 */
const LastFmSimilarTracksRail = ({ artist, title, limit = 12, onSaveToSpotify, onShareToDiscord }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [loadedFor, setLoadedFor] = useState(null);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [digging, setDigging] = useState(false);
  const queueId = useRef(`lfm-rail-${Math.random().toString(36).slice(2, 8)}`).current;
  const scrollRef = useRef(null);
  const cardId = (t, idx) => `lfm-${t.artist}-${t.track || idx}`;

  const fetchSimilar = async (signal) => {
    if (!artist || !title) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${BACKEND_URL}/api/lastfm/track/similar?artist=${encodeURIComponent(artist)}&track=${encodeURIComponent(title)}&limit=${limit}&electronic_only=true&with_covers=true`;
      const res = await fetch(url, { signal });
      if (!res.ok) throw new Error(`Last.fm rail HTTP ${res.status}`);
      const data = await res.json();
      setItems(Array.isArray(data?.track) ? data.track : []);
      setLoadedFor(`${artist}|${title}`);
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message || 'Failed to load similar tracks');
    } finally {
      setLoading(false);
    }
  };

  // Collapse again (and drop stale results) whenever the identified track changes.
  useEffect(() => {
    stopPreview();
    setOpen(false);
    setItems([]);
    setError(null);
    setLoadedFor(null);
  }, [artist, title]);

  useEffect(() => {
    if (!open || loadedFor === `${artist}|${title}`) return;
    const ctrl = new AbortController();
    fetchSimilar(ctrl.signal);
    return () => ctrl.abort();
    // eslint-disable-next-line
  }, [open, artist, title, limit]);

  // Mini-radio: register this rail's playlist so a finished preview rolls into the next.
  useEffect(() => {
    setPreviewQueue(queueId, items.map((t, idx) => ({
      id: cardId(t, idx), src: t.preview_url, artist: t.artist, title: t.track,
    })));
    return () => clearPreviewQueue(queueId);
  }, [items, queueId]);

  // Keep the playing card in view as the radio advances.
  useEffect(() => subscribePreview((id) => {
    setNowPlaying(id);
    if (!id || !scrollRef.current) return;
    const el = scrollRef.current.querySelector(`[data-card-id="${CSS.escape(id)}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }), []);

  if (!artist || !title) return null;

  const digTheRun = async () => {
    if (digging || !items.length) return;
    setDigging(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/crates/dig-run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('beatseek_token') || ''}` },
        body: JSON.stringify({
          seed_title: title,
          seed_artist: artist,
          tracks: items.filter(t => t.track).map(t => ({
            track_id: `${t.artist || ''}-${t.track || ''}`.toLowerCase().replace(/\s+/g, '-'),
            source: 'beatseek_radio',
            title: t.track,
            artist: t.artist || '',
            artwork_url: t.cover_url || null,
            preview_url: t.preview_url || null,
          })),
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || 'Could not save the run');
      toast.success(
        d.added ? `${d.added} track${d.added === 1 ? '' : 's'} dug into "${d.crate_name}"` : 'Already in that dig crate',
        { description: d.skipped ? `${d.skipped} already there` : 'Find it in Crate Box' }
      );
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDigging(false);
    }
  };

  return (
    <div data-testid="lastfm-similar-rail" style={{
      minWidth: 0, maxWidth: '100%', width: '100%',
      marginTop: '14px', padding: open ? '10px 12px' : '8px 12px', borderRadius: '12px',
      background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
        <button
          data-testid="lastfm-rail-toggle"
          onClick={() => setOpen(o => { if (o) stopPreview(); return !o; })}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none',
            padding: 0, cursor: 'pointer', color: 'rgba(255,255,255,0.78)', fontSize: '12px',
            fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          }}
        >
          {open ? <ChevronUp size={14} style={{ color: '#d51007' }} /> : <ChevronDown size={14} style={{ color: '#d51007' }} />}
          <span>Similar tracks</span>
          <span style={{ fontSize: '10px', color: '#8a8a8a', fontWeight: 500, letterSpacing: 0, textTransform: 'none' }}>
            via <span style={{ color: '#d51007', fontWeight: 700 }}>Last.fm</span>
          </span>
          <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', fontWeight: 400, letterSpacing: 0, textTransform: 'none' }}>
            — {open ? 'hide' : 'show'}
          </span>
        </button>

        {open && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {nowPlaying && (
              <span data-testid="lastfm-rail-radio-flag" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', color: '#00e5ff' }}>
                <Radio size={11} className="animate-pulse" /> RADIO
              </span>
            )}
            <button
              data-testid="lastfm-rail-dig-btn"
              onClick={digTheRun}
              disabled={digging || !items.length}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 999,
                background: 'rgba(247,213,92,0.08)', border: '1px solid rgba(247,213,92,0.4)',
                color: '#f7d55c', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
                cursor: digging ? 'wait' : 'pointer', opacity: items.length ? 1 : 0.4,
              }}
              title="Dig the whole run into its own crate"
            >
              <PackagePlus size={12} /> {digging ? 'Digging…' : 'Dig run'}
            </button>
            <button
              data-testid="lastfm-rail-refresh"
              onClick={() => fetchSimilar()}
              disabled={loading}
              style={{ background: 'none', border: 'none', cursor: loading ? 'wait' : 'pointer', color: '#888', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}
              title="Refresh"
            >
              <RefreshCcw size={11} className={loading ? 'animate-spin' : ''} />
              {loading ? '...' : 'Refresh'}
            </button>
          </div>
        )}
      </div>

      {open && (
        <div style={{ marginTop: '9px' }}>
          {!loading && (error || items.length === 0) ? (
            <p data-testid="lastfm-rail-empty" style={{ margin: 0, fontSize: '11px', fontWeight: 300, color: 'rgba(255,255,255,0.4)' }}>
              Last.fm has no electronic matches for this one yet.
            </p>
          ) : (
            <div ref={scrollRef} style={{
              display: 'flex', gap: '7px', overflowX: 'auto', overflowY: 'hidden',
              paddingBottom: '5px', scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch', touchAction: 'pan-x',
            }}>
              {loading && items.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                <div key={`sk-${i}`} style={{
                  flex: '0 0 auto', width: '132px', height: '178px', borderRadius: '9px',
                  background: 'linear-gradient(90deg, rgba(255,255,255,0.04), rgba(255,255,255,0.08), rgba(255,255,255,0.04))',
                  backgroundSize: '200% 100%', animation: 'shimmer 1.4s linear infinite',
                }} />
              ))}

              {items.map((t, idx) => {
                const imgList = Array.isArray(t.image) ? t.image : [];
                const lastfmCover = imgList.length > 0 ? (imgList[imgList.length - 1]?.['#text'] || imgList[0]?.['#text']) : null;
                const cover = t.cover_url || lastfmCover;
                const matchPct = t.match ? Math.round(parseFloat(t.match) * 100) : null;
                const cid = cardId(t, idx);
                const isPlaying = nowPlaying === cid;
                return (
                  <a
                    key={`${t.artist}-${t.track || 'a'}-${idx}`}
                    href={t.url || `https://www.last.fm/music/${encodeURIComponent(t.artist)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`lastfm-similar-card-${idx}`}
                    data-card-id={cid}
                    style={{
                      flex: '0 0 auto', width: '132px', padding: '6px', borderRadius: '9px',
                      background: isPlaying ? 'rgba(0,229,255,0.09)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${isPlaying ? 'rgba(0,229,255,0.45)' : 'rgba(255,255,255,0.06)'}`,
                      textDecoration: 'none', color: '#fff',
                      transition: 'transform 0.2s, background-color 0.2s, border-color 0.2s',
                      display: 'flex', flexDirection: 'column', gap: '5px',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(213,16,7,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.background = isPlaying ? 'rgba(0,229,255,0.09)' : 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = isPlaying ? 'rgba(0,229,255,0.45)' : 'rgba(255,255,255,0.06)'; }}
                  >
                    <CoverTile src={cover} seed={`${t.artist}${t.track || ''}`} label={t.artist}>
                      {matchPct !== null && (
                        <span style={{ position: 'absolute', top: '3px', right: '3px', padding: '1px 5px', borderRadius: '7px', fontSize: '8px', fontWeight: 700, background: 'rgba(0,0,0,0.7)', color: '#06b6d4' }}>{matchPct}%</span>
                      )}
                      {t.preview_url || (t.artist && t.track) ? (
                        <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <PreviewPlayButton src={t.preview_url} id={cid} queueId={queueId} artist={t.artist} title={t.track} size={13} testId={`lastfm-preview-${idx}`} />
                        </span>
                      ) : (
                        <ExternalLink size={10} style={{ position: 'absolute', bottom: '3px', right: '3px', color: 'rgba(255,255,255,0.5)' }} />
                      )}
                    </CoverTile>

                    <div style={{ fontSize: '10px', fontWeight: 700, lineHeight: 1.25, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {t.track || `${t.artist}`}
                    </div>
                    <div style={{ fontSize: '9px', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {t.track ? t.artist : 'Artist'}
                    </div>
                    {Array.isArray(t.tags) && t.tags.length > 0 && (
                      <div style={{ fontSize: '8px', color: '#06b6d4', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        #{t.tags.slice(0, 2).join(' #')}
                      </div>
                    )}
                    <RailTrackActions
                      track={{ ...t, title: t.track, artwork: t.cover_url }}
                      idx={idx}
                      onSaveToSpotify={onSaveToSpotify}
                      onShareToDiscord={onShareToDiscord}
                    />
                  </a>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LastFmSimilarTracksRail;
